import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useParams } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";

// Helper functions
const resolveItemFromResponse = (response) => {
  if (!response) return null;
  if (response.item) return response.item;
  if (Array.isArray(response.items)) return response.items[0] || null;
  return response;
};

const resolveDirectionValue = (value) =>
  typeof value === "string" && value.toUpperCase() === "RTL" ? "RTL" : "LTR";

const extractSentenceTokens = (sentence) =>
  typeof sentence === "string"
    ? sentence.trim().split(/\s+/).filter(Boolean)
    : [];

const normalisePointsValue = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

const createEmptyItem = () => ({
  Type: "ReArrange",
  Name: "",
  Title: "",
  Direction: "LTR",
  points: 1,
  skilltype: "",
  itemcat: "",
  Options: [{ id: 1, Value: "", Rationale: "" }],
});

const ReArrangeItemDetail = () => {
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";

  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(createEmptyItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewOrder, setPreviewOrder] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (!isNew) {
          const data = await ItemService.fetchItemById(itemId);
          const resolved = resolveItemFromResponse(data);
          setItem({
            ...resolved,
            skilltype: normalizeSkillTypeValue(resolved.skilltype),
          });
        }
        const taxonomy = await ItemService.fetchTaxonomy("arabic");
        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomy));
      } catch (err) {
        setSaveError("Failed to load item or taxonomy.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [itemId, isNew]);

  const directionValue = useMemo(
    () => resolveDirectionValue(item.Direction ?? item.direction),
    [item.Direction, item.direction]
  );
  const isRtlDirection = directionValue === "RTL";

  // Tokenize sentence for preview
  const previewTokens = useMemo(() => {
    const value = item?.Options?.[0]?.Value || "";
    return extractSentenceTokens(value);
  }, [item?.Options]);

  useEffect(() => {
    setPreviewOrder(previewTokens.slice());
  }, [previewTokens]);

  const handleFieldChange = (key, value) => {
    setItem((p) => ({ ...p, [key]: value }));
  };

  const handleOptionChange = (index, field, value) => {
    setItem((prev) => {
      const opts = [...(prev.Options || [])];
      opts[index] = { ...opts[index], [field]: value };
      return { ...prev, Options: opts };
    });
  };

  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();
    // const AUTHOR_ID = "57b99ce3-f2ef-4af5-a1ca-bfd1a4368d02";
    const normalizedPoints = normalisePointsValue(item.points);

    // ✅ Final payload with exact key order
    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: `item#${itemId || "new"}`,
      createdBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: `item#${itemId || "new"}`,

      // ✅ Add these lines
      itemcat: item.itemcat,
      skilltype: item.skilltype,

      Name: item.Name,
      Options: item.Options.map((opt, i) => ({
        id: i + 1,
        Rationale: opt.Rationale || "",
        Value: opt.Value || "",
      })),
      points: normalizedPoints,
      Title:
        item.Title || "<div>Arrange the words to form correct sentence</div>",
      Type: "ReArrange",
      version: "v1",
    };

    try {
      setSaving(true);
      await ItemService.saveItem(payload, isNew ? undefined : itemId);
      setSaveSuccess(true);
      setSaveError(null);
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, isNew, itemId]);

  const jsonPreview = useMemo(() => {
    const timestamp = new Date().toISOString();
    return JSON.stringify(
      {
        PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
        SK: `item#${itemId || "new"}`,
        dateCreated: timestamp,
        dateUpdated: timestamp,
        GPK1: "item",
        GSK1: `item#${itemId || "new"}`,
        Name: item.Name,
        Options: item.Options.map((opt, i) => ({
          id: i + 1,
          Rationale: opt.Rationale || "",
          Value: opt.Value || "",
        })),
        points: item.points,
        Title:
          item.Title || "<div>Arrange the words to form correct sentence</div>",
        Type: "ReArrange",
        version: "v1",
      },
      null,
      2
    );
  }, [item, itemId]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading item details...</Typography>
      </Paper>
    );

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="ReArrange" color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {isNew ? "Create ReArrange Item" : "Edit ReArrange Item"}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              onClick={() => setJsonDialogOpen(true)}
              disabled={!item}
            >
              View JSON
            </Button>
          </Stack>

          {saveSuccess && (
            <Alert severity="success">Item saved successfully!</Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          {/* Basic Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                required
                fullWidth
                value={item.Name}
                onChange={(e) =>
                  setItem((p) => ({ ...p, Name: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                required
                value={item.points}
                onChange={(e) =>
                  setItem((p) => ({ ...p, points: Number(e.target.value) }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  label="Skill Type"
                  onChange={(e) =>
                    setItem((p) => ({
                      ...p,
                      skilltype: normalizeSkillTypeValue(e.target.value),
                    }))
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  label="Item Category"
                  onChange={(e) =>
                    setItem((p) => ({ ...p, itemcat: e.target.value }))
                  }
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Prompt Editor */}
          <Divider />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Prompt / Question Editor
            </Typography>
            <ReactQuill
              theme="snow"
              value={item.Title}
              onChange={(value) => setItem((p) => ({ ...p, Title: value }))}
              placeholder="Write your question or instructions here..."
              style={{ height: "200px", marginBottom: "50px" }}
            />
          </Box>

          {/* Sentence Input */}
          <Divider />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Correct Sentence
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={item.Options?.[0]?.Value || ""}
              onChange={(e) => handleOptionChange(0, "Value", e.target.value)}
              placeholder="Type the correct sentence here..."
              inputProps={{
                dir: isRtlDirection ? "rtl" : "ltr",
                style: { textAlign: isRtlDirection ? "right" : "left" },
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Rationale (Optional)"
              value={item.Options?.[0]?.Rationale || ""}
              onChange={(e) =>
                handleOptionChange(0, "Rationale", e.target.value)
              }
              placeholder="Add an explanation or rationale for this sentence..."
              sx={{ mt: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              You can switch between Right-to-Left or Left-to-Right direction
              below.
            </Typography>
          </Box>

          {/* Common Direction Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={isRtlDirection}
                onChange={() =>
                  handleFieldChange("Direction", isRtlDirection ? "LTR" : "RTL")
                }
              />
            }
            label="Right-to-left direction"
          />

          {/* Preview */}
          <Divider />
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "background.default",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" fontWeight={600}>
                  Preview
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setPreviewOrder(previewTokens.slice())}
                  title="Reset order"
                >
                  <RestartAltIcon />
                </IconButton>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Drag and drop to preview how learners will rearrange the
                sentence. This does not affect saved data.
              </Typography>

              {previewOrder.length ? (
                <DragDropContext
                  onDragEnd={(result) => {
                    if (!result.destination) return;
                    const src = result.source.index;
                    const dest = result.destination.index;
                    setPreviewOrder((prev) => {
                      const next = [...prev];
                      const [moved] = next.splice(src, 1);
                      next.splice(dest, 0, moved);
                      return next;
                    });
                  }}
                >
                  <Droppable
                    droppableId="preview-tokens"
                    direction="horizontal"
                  >
                    {(provided) => (
                      <Stack
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{
                          direction: isRtlDirection ? "rtl" : "ltr",
                          justifyContent: isRtlDirection
                            ? "flex-end"
                            : "flex-start",
                          minHeight: 40,
                        }}
                      >
                        {previewOrder.map((token, index) => (
                          <Draggable
                            key={`${token}-${index}`}
                            draggableId={`${token}-${index}`}
                            index={index}
                          >
                            {(dragProvided, snapshot) => (
                              <Box
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                sx={{
                                  transform: snapshot.isDragging
                                    ? "scale(1.05)"
                                    : "none",
                                  transition: "transform 100ms linear",
                                }}
                              >
                                <Chip
                                  label={token}
                                  variant="outlined"
                                  sx={{ px: 1.25, py: 0.5, fontSize: 14 }}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <Alert severity="info">
                  Add a sentence above to see preview words.
                </Alert>
              )}
            </Stack>
          </Paper>

          {/* Save */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Item"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* JSON Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ReArrangeItemDetail;
